import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './DietaryRecipes.css'; // 新建CSS文件
import apiClient from '../services/apiClient';

interface Recipe {
  id: string;
  title: string;
  categories: string[];
  content: string;
}
const PAGE_SIZE = 6;

const DietaryRecipes: React.FC = () => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // 为标签分配独特颜色的函数
  const getTagColor = (tag: string): string => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
    ];
    const index = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
  const response = await apiClient.get<Recipe[]>('/diet_recipes');
  const fetchedRecipes: Recipe[] = response.data;
        setRecipes(fetchedRecipes);
        setFilteredRecipes(fetchedRecipes);

        // 提取所有不重复的类别
        const allCategories = new Set<string>();
        fetchedRecipes.forEach(recipe => {
          recipe.categories.forEach(category => allCategories.add(category));
        });
        setCategories(['所有类别', ...Array.from(allCategories).sort()]);

      } catch (err) {
        setError('加载药膳方失败');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRecipes();
  }, []);

  useEffect(() => {
    let filtered = recipes;

    // 标签过滤
    if (selectedTags.length > 0) {
      filtered = filtered.filter(recipe =>
        selectedTags.every(tag => recipe.categories.includes(tag))
      );
    }

    // 名称搜索过滤
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(recipe =>
        recipe.title.toLowerCase().includes(query)
      );
    }

    setFilteredRecipes(filtered);
  }, [selectedTags, searchQuery, recipes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTags, searchQuery, recipes.length]);

  const heroStats = useMemo(() => [
    { label: '收录药膳', value: recipes.length },
    { label: '筛选结果', value: filteredRecipes.length },
    { label: '已选标签', value: selectedTags.length },
  ], [recipes.length, filteredRecipes.length, selectedTags.length]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredRecipes.length / PAGE_SIZE)),
    [filteredRecipes.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRecipes = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredRecipes.slice(start, end);
  }, [filteredRecipes, currentPage]);

  const gotoPage = (page: number) => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(safePage);
  };

  const availableTagOptions = useMemo(() =>
    categories.filter((category) => category !== '所有类别'),
    [categories]
  );

  const popularTags = useMemo(() => {
    const counts = new Map<string, number>();
    recipes.forEach((recipe) => {
      recipe.categories.forEach((cat) => {
        counts.set(cat, (counts.get(cat) || 0) + 1);
      });
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));
  }, [recipes]);

  const toggleTagSelection = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  if (isLoading) return (
    <div className="unified-page-wrapper">
      <div className="unified-page-header">
        <h2>药膳方列表</h2>
      </div>
      <div className="unified-page-content-scrollable">
        加载中...
      </div>
    </div>
  );
  if (error) return (
    <div className="unified-page-wrapper">
      <div className="unified-page-header">
        <h2>药膳方列表</h2>
      </div>
      <div className="unified-page-content-scrollable error">
        {error}
      </div>
    </div>
  );

  return (
    <motion.div
      className="unified-page-wrapper recipes-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="unified-page-header recipes-hero">
        <div className="recipes-hero-text">
          <span className="hero-badge">Medicinal Cuisine Lab</span>
          <h2>药膳方匠心集</h2>
          <p className="hero-sub">从传统经典到现代调养，精选药膳方一览，助力体质辨识与膳食调理。</p>
          <div className="hero-tags">
            <span>四季养生</span>
            <span>证候对应</span>
            <span>AI菜单建议</span>
          </div>
        </div>
        <div className="hero-stats">
          {heroStats.map((stat) => (
            <div key={stat.label} className="hero-stat-card">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="unified-page-content-scrollable recipes-scroll">
        <div className="recipes-layout">
          <motion.div
            className="recipes-filter-card"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="panel-header">
              <h3>快速检索药膳</h3>
              <span className="panel-caption">输入关键字或选择适配的功效标签</span>
            </div>
            <div className="search-stack">
              <div className="search-input-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  id="search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="输入药膳方名称或功效..."
                  className="search-input"
                />
              </div>

              {popularTags.length > 0 && (
                <div className="popular-tags">
                  <span className="tag-caption">热门标签</span>
                  <div className="tag-chip-list">
                    {popularTags.map((item) => (
                      <button
                        key={item.name}
                        type="button"
                        className={`tag-chip ${selectedTags.includes(item.name) ? 'active' : ''}`}
                        style={selectedTags.includes(item.name) ? { backgroundColor: getTagColor(item.name) } : undefined}
                        onClick={() => toggleTagSelection(item.name)}
                      >
                        <span>{item.name}</span>
                        <small>{item.count}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {availableTagOptions.length > 0 && (
                <div className="all-tags">
                  <span className="tag-caption">全部分类</span>
                  <div className="tag-chip-list scrollable">
                    {availableTagOptions.map((category) => (
                      <button
                        key={category}
                        type="button"
                        className={`tag-chip ${selectedTags.includes(category) ? 'active' : ''}`}
                        style={selectedTags.includes(category) ? { backgroundColor: getTagColor(category) } : undefined}
                        onClick={() => toggleTagSelection(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedTags.length > 0 && (
                <div className="selected-tag-summary">
                  <span className="tag-caption">已选择</span>
                  <div className="selected-tags-container">
                    {selectedTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="selected-tag"
                        style={{ backgroundColor: getTagColor(tag) }}
                        onClick={() => toggleTagSelection(tag)}
                      >
                        {tag}
                        <span className="remove-icon">×</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="clear-tags"
                      onClick={() => setSelectedTags([])}
                    >
                      清除全部
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="recipes-results"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="results-toolbar">
              <div>
                <h3>药膳方列表</h3>
                <p>为不同体质与证候提供配伍思路</p>
              </div>
              <div className="toolbar-meta">
                <span>共 {filteredRecipes.length} 个方案</span>
                {selectedTags.length > 0 && (
                  <span className="meta-tag">筛选：{selectedTags.join('、')}</span>
                )}
              </div>
            </div>

            <div className="recipe-grid">
              {filteredRecipes.length > 0 ? (
                paginatedRecipes.map(recipe => (
                  <Link to={`/app/dietary-recipes/${recipe.id}`} key={recipe.id} className="recipe-card-link">
                    <div className="recipe-card">
                      <div className="recipe-card-header">
                        <h3>{recipe.title}</h3>
                        <span className="card-arrow">→</span>
                      </div>
                      <div className="recipe-badges">
                        {recipe.categories.slice(0, 5).map((category) => (
                          <span
                            key={category}
                            className="recipe-badge"
                            style={{ backgroundColor: getTagColor(category) }}
                          >
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="empty-state">没有找到相关药膳方，请尝试其他组合。</div>
              )}
            </div>

            {filteredRecipes.length > PAGE_SIZE && (
              <div className="pagination-bar">
                <button
                  type="button"
                  className="pager-btn"
                  onClick={() => gotoPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  上一页
                </button>
                <div className="pager-info">
                  第 <strong>{currentPage}</strong> / {totalPages} 页
                </div>
                <button
                  type="button"
                  className="pager-btn"
                  onClick={() => gotoPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  下一页
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default DietaryRecipes;
